/**
 * Lincoln mark — abstract voice orb (navy/yellow brand mark).
 *
 * No face. Just a navy gradient sphere with an animated yellow waveform
 * core, concentric voice rings, and (optionally) pulse rings outside and
 * a mic FAB chip in the lower-right.
 */
export default function LincolnAvatar({ size = 200, pulse = false, voice = true, withMicChip = true }) {
  const heights = [0.45, 0.75, 1.0, 0.6, 0.9, 0.55, 1.0, 0.7, 0.4];
  return (
    <div className="relative inline-block" style={{ width: size, height: size }}>
      {pulse && [0, 1, 2].map((i) => (
        <div
          key={i}
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            border: '1.5px solid rgba(240,184,12,0.4)',
            animation: `lc-pulse-ring 2.4s ${i * 0.8}s ease-out infinite`,
          }}
        />
      ))}

      {/* Outer halo */}
      <div
        className="absolute rounded-full pointer-events-none"
        style={{
          inset: `-${size * 0.04}px`,
          background: 'radial-gradient(circle, rgba(240,184,12,0.18) 0%, transparent 70%)',
        }}
      />

      {/* The orb */}
      <div
        className="absolute inset-0 rounded-full overflow-hidden"
        style={{
          background: 'radial-gradient(circle at 30% 25%, #2a4a78 0%, #102a4a 45%, #0b1f38 100%)',
          boxShadow: `inset 0 -${size * 0.05}px ${size * 0.18}px rgba(0,0,0,0.45), inset 0 ${size * 0.04}px ${size * 0.14}px rgba(255,255,255,0.1), 0 ${size * 0.06}px ${size * 0.18}px rgba(11,31,54,0.35)`,
        }}
      >
        <div
          className="absolute rounded-full"
          style={{
            inset: `${size * 0.1}px`,
            background: 'radial-gradient(circle at 50% 45%, rgba(240,184,12,0.22) 0%, transparent 70%)',
          }}
        />
        {[0.55, 0.7, 0.85].map((r, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{ inset: `${(1 - r) * 50}%`, border: `1px solid rgba(240,184,12,${0.18 - i * 0.04})` }}
          />
        ))}

        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ gap: size * 0.03 }}
        >
          {heights.map((h, i) => (
            <span
              key={i}
              className="block rounded-sm"
              style={{
                width: Math.max(2, size * 0.025),
                height: `${h * size * 0.42}px`,
                background: 'linear-gradient(180deg, #f5c63a 0%, #f0b80c 60%, #d99a06 100%)',
                borderRadius: size * 0.012,
                boxShadow: '0 0 6px rgba(240,184,12,0.45)',
                transformOrigin: 'center',
                animation: voice ? `lc-wave 1.3s ${i * 0.09}s ease-in-out infinite` : 'none',
              }}
            />
          ))}
        </div>

        {/* Top-light gloss */}
        <div
          className="absolute pointer-events-none rounded-full"
          style={{
            left: '12%', right: '12%', top: '6%', height: '30%',
            background: 'radial-gradient(ellipse at center top, rgba(255,255,255,0.18) 0%, transparent 70%)',
          }}
        />
      </div>

      {/* Mic FAB chip */}
      {withMicChip && (
        <div
          className="absolute flex items-center justify-center rounded-full"
          style={{
            bottom: `${size * 0.02}px`,
            right: `${size * 0.02}px`,
            width: size * 0.3,
            height: size * 0.3,
            background: 'linear-gradient(135deg, #f5c63a 0%, #d99a06 100%)',
            boxShadow: `0 ${size * 0.04}px ${size * 0.1}px rgba(240,184,12,0.5)`,
            border: `${Math.max(2, size * 0.018)}px solid #0b1f38`,
          }}
        >
          <svg width={size * 0.15} height={size * 0.15} viewBox="0 0 24 24" fill="none" stroke="#0b1f38" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
        </div>
      )}
    </div>
  );
}
